import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { DatasetsDto, Dataset, DatasetReferenceDto, DatasetIdRequest, CopyDatasetDto, ExtractDatasetDto } from './models/datasets.models';
import { CreateViewDto, UpdateViewDto, DatasetViewDefinition } from './models/view-definition.models';
import { Observable, of, throwError } from 'rxjs';
import { apiEndpoints } from 'src/app/api-endpoints';
import { Store, select } from '@ngrx/store';
import { BoardState } from '../board/+store/board-state/board-state.reducer';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { loadDatasetsSuccess, clearDatasets, updateDatasetSuccess, deleteDatasetSuccess } from '../board/+store/board-state/board-state.actions';
import { selectDatasetList } from '../board/+store/board-state/board-state.selectors';
import { ApplicationIdRequest } from './models/application-details.model';

@Injectable({
  providedIn: 'root'
})
export class DatasetService {
  private apiUrl = environment.apiConfig.baseUri + apiEndpoints.databaseAdmin;
  private datasetApis = {
    createDatasetReference: '/create-dataset-reference',
    updateDatasetReference: '/update-dataset-reference-and-schema',
    deleteDatasetReference: '/delete-dataset-reference',
    getDatasetReferences: '/get-datasets-for-application',
    copyDataset: '/copy-dataset',
    extractDataset: '/extract-dataset',
    createDatasetView: '/create-dataset-view',
    updateDatasetView: '/update-dataset-view',
    getViewDefinition: '/get-view-definition',
  };

  constructor(
    private http: HttpClient,
    private boardStore: Store<BoardState>
  ) {}

  /**
   * Retrieves the dataset references for the given application.
   *
   * - If the BoardState already contains datasets, returns them immediately.
   * - Otherwise, calls the backend API, dispatches an action to store the results, and returns the data.
   */
  getDatasetReferences(): Observable<Map<number, Dataset>> {
    return this.boardStore.pipe(
      select(selectDatasetList),
      take(1), // Take the current state once
      switchMap(datasetMap => {
        if (datasetMap && datasetMap.size > 0) {
          // Datasets already exist in the store: return them as a DatasetReferencesResponse.
          return of(datasetMap);
        } else {
          return this.http.post<DatasetsDto>(
            `${this.apiUrl}${this.datasetApis.getDatasetReferences}`,
            {}
          ).pipe(
            filter(response => response !== null),
            map(datasets => {
              let datasetMap = new Map<number, Dataset>();
              datasets.datasetReferences.forEach(element => {

                datasetMap.set(
                  element.datasetReferenceTable.datasetId,
                  new Dataset(element.datasetReferenceTable, element.dataTableSchemas)
                )
              });
              return datasetMap;
            }),
            tap(datasetsMap => {
              // Dispatch an action to store the datasets in the state.
              this.boardStore.dispatch(loadDatasetsSuccess({ datasets: datasetsMap }));
            })
          );
        }
      }
    ));
  }

  /**
  * Updates the dataset reference for the given dataset.
  *
  * - Updates BoardState upon success
  */
  updateDatasetReference(dataset: DatasetReferenceDto): Observable<DatasetReferenceDto> {
    return this.http.post<DatasetReferenceDto>(
      `${this.apiUrl}${this.datasetApis.updateDatasetReference}`,
      dataset
    ).pipe(
      tap((response: DatasetReferenceDto) => {
        // Dispatch an action to store the datasets in the state.
        if(response){
          // Initialize missing fields if backend doesn't return them
          this.initializeMissingFields(response);
          //Tap into response as this response has all the internalIds generated
          this.boardStore.dispatch(updateDatasetSuccess({ dataset: response }));
        }
      }),
      catchError((error) => {
        // Handle the error here, such as logging it or displaying an error message
        console.error('Error updating dataset reference:', error);
        // Return a safe fallback or rethrow the error as needed
        return throwError(() => new Error('Error updating dataset reference'));
      })
    );
  }

  /**
   * Creates the new dataset reference in the backend.
   *
   * - Updates BoardState upon success
   */
  createDatasetReference(dataset: DatasetReferenceDto): Observable<DatasetReferenceDto> {
    return this.http.post<DatasetReferenceDto>(
      `${this.apiUrl}${this.datasetApis.createDatasetReference}`,
      dataset
    ).pipe(
      tap((response: DatasetReferenceDto) => {
        // Dispatch an action to store the datasets in the state.
        if(response){
          // Initialize missing fields if backend doesn't return them
          this.initializeMissingFields(response);
          //Tap into response as this response has all the internalIds generated
          this.boardStore.dispatch(updateDatasetSuccess({ dataset: response }));
        }
      })
    );
  }

  /**
  * Creates the new dataset reference in the backend.
  *
  * - Updates BoardState upon success
  */
  deleteDatasetReference(datasetId: number): Observable<boolean> {
    return this.http.post<boolean>(
      `${this.apiUrl}${this.datasetApis.deleteDatasetReference}`,
      new DatasetIdRequest(datasetId)
    ).pipe(
      tap((success: boolean) => {
        // Dispatch an action to store the datasets in the state.
        if(success){
          //Tap into response as this response has all the internalIds generated
          this.boardStore.dispatch(deleteDatasetSuccess({ datasetId: datasetId }));
        }
      })
    );
  }

  /**
   * Clears the dataset references stored in the BoardState.
   */
  clearDatasetReferences(): void {
    this.boardStore.dispatch(clearDatasets());
  }

  /**
   * Initialize missing fields for datasets returned from backend
   */
  private initializeMissingFields(dataset: DatasetReferenceDto): void {
    if (!dataset.datasetReferenceTable.allowedViewRoles || dataset.datasetReferenceTable.allowedViewRoles.length === 0) {
      // Default: Reader and above for view permissions
      dataset.datasetReferenceTable.allowedViewRoles = ['Reader', 'Writer', 'Admin', 'Owner'];
    }
    if (!dataset.datasetReferenceTable.allowedEditRoles || dataset.datasetReferenceTable.allowedEditRoles.length === 0) {
      // Default: Writer and above for edit permissions
      dataset.datasetReferenceTable.allowedEditRoles = ['Writer', 'Admin', 'Owner'];
    }
    if (!dataset.datasetReferenceTable.allowedViewEmails) {
      dataset.datasetReferenceTable.allowedViewEmails = [];
    }
    if (!dataset.datasetReferenceTable.allowedEditEmails) {
      dataset.datasetReferenceTable.allowedEditEmails = [];
    }

    // Initialize fieldToDisplay for each schema
    if (dataset.dataTableSchemas) {
      dataset.dataTableSchemas.forEach(schema => {
        if (schema.fieldToDisplay === undefined || schema.fieldToDisplay === null) {
          schema.fieldToDisplay = '';
        }
      });
    }
  }

  /**
   * Copies a dataset including its metadata, schema, and all row data.
   * @param applicationId The application ID
   * @param sourceDatasetId The dataset ID to copy from
   * @param newDatasetName The name for the new dataset
   */
  copyDataset(applicationId: number, sourceDatasetId: number, newDatasetName: string): Observable<DatasetReferenceDto> {
    const request = new CopyDatasetDto();
    request.applicationId = applicationId;
    request.sourceDatasetId = sourceDatasetId;
    request.newDatasetName = newDatasetName;

    return this.http.post<DatasetReferenceDto>(
      `${this.apiUrl}${this.datasetApis.copyDataset}`,
      request
    );
  }

  /**
   * Extracts fields from a wide source table into a new normalized table.
   * @param request The extract configuration specifying source, keys, categories, and field groups
   */
  extractDataset(request: ExtractDatasetDto): Observable<DatasetReferenceDto> {
    return this.http.post<DatasetReferenceDto>(
      `${this.apiUrl}${this.datasetApis.extractDataset}`,
      request
    );
  }

  /**
   * Creates a new view dataset from a structured definition (sources, joins, projections, filters).
   */
  createDatasetView(request: CreateViewDto): Observable<DatasetReferenceDto> {
    return this.http.post<DatasetReferenceDto>(
      `${this.apiUrl}${this.datasetApis.createDatasetView}`,
      request
    ).pipe(
      tap(response => {
        const dataset = new Dataset(response.datasetReferenceTable, response.dataTableSchemas);
        this.boardStore.dispatch(updateDatasetSuccess({ dataset }));
      })
    );
  }

  /**
   * Updates an existing view dataset definition.
   */
  updateDatasetView(request: UpdateViewDto): Observable<DatasetReferenceDto> {
    return this.http.post<DatasetReferenceDto>(
      `${this.apiUrl}${this.datasetApis.updateDatasetView}`,
      request
    ).pipe(
      tap(response => {
        const dataset = new Dataset(response.datasetReferenceTable, response.dataTableSchemas);
        this.boardStore.dispatch(updateDatasetSuccess({ dataset }));
      })
    );
  }

  /**
   * Retrieves the structured definition of a view dataset for editing.
   */
  getViewDefinition(applicationId: number, datasetId: number): Observable<DatasetViewDefinition> {
    return this.http.post<DatasetViewDefinition>(
      `${this.apiUrl}${this.datasetApis.getViewDefinition}`,
      { applicationId, datasetId }
    );
  }
}
